import { Typography, Grid, Box, CircularProgress, Alert, Autocomplete, TextField, IconButton, Tooltip } from "@mui/material";
import { useEffect, useState, useMemo, useRef } from "react";
import { StyledBox, StyledTypography, StyledPaper, StyledDataGrid } from "./styles";
import { useNavigate } from "react-router-dom";

//components
import Title from "../../components/Typography/Title";
import TileHeading from "../../components/Typography/TileHeading";
import Texas100RoadSegmentsMap from "../../components/Map/Texas100RoadSegmentsMap";
import CustomToolbar from "../../components/Toolbar/CustomToolbar";

//context
import { useAuth } from "../../context/AuthContext";

//custom hooks
import { useFetch } from "../../hooks/useFetch";

//constants
import { API_URLS } from "../../Constants";

//theme
import themes from "../../themes/defaultTheme";

//icons
import { WarningAmber as NeedsReviewIcon, Edit as EditIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from "@mui/icons-material";

//utils
import { markForReview } from "../../utils/dateUtils";
import { getFormattedDate } from "../../utils/getFormattedDateTime";

export default function Dashboard() {
    const navigate = useNavigate();
    const { auth } = useAuth();
    const firstName = auth?.user?.firstName;
    const loggedInUser = auth?.user || null;
    const loggedInUserRole = auth?.user?.roleName || null;
    const loggedInUserRegionIds = loggedInUser?.regionAccessibleSet
        ? loggedInUser.regionAccessibleSet.map(reg => reg.id)
        : [];

    //fetchers
    const roadwaysFetcher = useFetch("get", API_URLS["GET_TOP_105_ROADWAYS"]);
    const statsFetcher = useFetch("get", API_URLS["API_GET_DASHBOARD_STATS"]);
    const projectsFetcher = useFetch("get", API_URLS["API_GET_PROJECTS"]);

    //state variables
    const [top105RoadwaysData, setTop105RoadwaysData] = useState([]);
    const [featureId, setFeatureId] = useState(null);
    const [regionsDropDown, setRegionsDropDown] = useState([]);
    const [selectedRegion, setSelectedRegion] = useState('');
    const [allStats, setAllStats] = useState([]);
    const [selectedStat, setSelectedStat] = useState({});
    const [pageTitle, setPageTitle] = useState('');
    const [projectsData, setProjectsData] = useState([]);
    const [showNeedsReview, setShowNeedsReview] = useState(false);
    const [activeFilter, setActiveFilter] = useState({ column: '', value: '' });

    //references
    const needsReviewTableRef = useRef(null);
    const isInitialMount = useRef(true);
    const layerRefs = useRef({});

    //Scroll to table when user toggles it open (skip initial mount)
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }
        if (showNeedsReview) {
            const timer = setTimeout(() => {
                needsReviewTableRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [showNeedsReview]);

    //Needs Review rows sorted by region name
    const needsReviewRows = useMemo(() => {
        return projectsData
            .filter(p => p.isPendingReview)
            .toSorted((a, b) => {
                const aRegion = (a.regionNames && a.regionNames.length > 0) ? a.regionNames[0] : '';
                const bRegion = (b.regionNames && b.regionNames.length > 0) ? b.regionNames[0] : '';
                return aRegion.localeCompare(bRegion);
            });
    }, [projectsData]);

    //Needs Review rows filtered by selected region
    const filteredNeedsReviewRows = useMemo(() => {
        let data = needsReviewRows || [];

        //apply selected region drop down filter
        if (selectedRegion && selectedRegion.toLowerCase() !== 'all') {
            data = data.filter(p => {
                const regions = p.regionNames || [];
                return [...regions].some(r => r === selectedRegion);
            });
        }

        //apply toolbar filter
        if (activeFilter.column && activeFilter.value) {
            const searchTerm = activeFilter.value.toLowerCase();
            data = data.filter(row => {
                const rawValue = row[activeFilter.column];
                const cellValue = String(rawValue || "").toLowerCase();
                return cellValue.includes(searchTerm);
            });
        }
        return data;
    }, [needsReviewRows, selectedRegion, activeFilter]);

    //Needs Review columns
    const needsReviewColumns = [
        {
            field: 'projectTitle',
            headerName: 'Project Title',
            flex: 1,
            minWidth: 450,
            renderCell: (params) => (
                <StyledTypography variant="body2">
                    {params.value || ""}
                </StyledTypography>
            ),
        },
        {
            field: 'regionName',
            headerName: 'Impacted Region(s)',
            flex: 0.5,
            minWidth: 250,
            filterable: false,
            valueGetter: (params) => {
                let regions = params.row.regionNames?.join(", ") || "";

                // Add impacted regions if present
                const impactedRegions = params.row.impactedRegions?.filter((reg) => reg.id !== null) || [];
                if (impactedRegions.length > 0) {
                    const impactedRegionNames = impactedRegions.map((reg) => reg.name).join(", ");
                    regions = regions ? `${regions}, ${impactedRegionNames}` : impactedRegionNames;
                }

                return regions;
            },
            renderCell: (params) => (
                <StyledTypography variant="body2">
                    {params.value || ""}
                </StyledTypography>
            ),
        },
        {
            field: 'reviewedDate',
            headerName: 'Good As Of',
            flex: 0.5,
            minWidth: 150,
            valueGetter: (params) => {
                return params.row.reviewedDate ? getFormattedDate(new Date(params.row.reviewedDate)) : null;
            },
            renderCell: (params) => (
                <StyledTypography variant="body2">
                    {params.value || ""}
                </StyledTypography>
            ),
        },
        {
            field: 'actions',
            headerName: 'Edit',
            sortable: false,
            filterable: false,
            valueGetter: (params) => {
                const projectImpactedRegions = params.row.impactedRegions?.length > 0 ? params.row.impactedRegions.filter(reg => reg.id !== null)?.map(reg => reg.id) : [];

                //Check if any of the loggedin user's region is one of the project's impacted region(s).
                if (loggedInUserRole.toLowerCase() !== "super admin" && loggedInUserRole.toLowerCase() !== "tti user"
                    && projectImpactedRegions && loggedInUserRegionIds.some(userReg => projectImpactedRegions.includes(userReg))) {
                    return null;
                }
                return `${window.location.origin}/edit-project?id=${params.row.id}`;
            },
            renderCell: (params) => {
                let disableEdit = false;

                //Check if any of the loggedin user's region is one of the project's impacted region(s).
                const projectImpactedRegions = params.row.impactedRegions?.length > 0 ? params.row.impactedRegions.filter(reg => reg.id !== null)?.map(reg => reg.id) : [];
                if ((loggedInUserRole.toLowerCase() !== "super admin" && loggedInUserRole.toLowerCase() !== "tti user"
                    && projectImpactedRegions && loggedInUserRegionIds.some(userReg => projectImpactedRegions.includes(userReg)))) {
                    disableEdit = true;
                }
                return (
                    <IconButton size="small" aria-label={`Edit ${params.row.projectTitle}`} onClick={() => navigate(`/edit-project?id=${params.row.id}`)} disabled={disableEdit} >
                        <EditIcon fontSize="small" />
                    </IconButton>
                );
            },
        }
    ];

    //get all projects for the logged in user.
    useEffect(() => {
        projectsFetcher.setRequest({});
    }, []);

    //Once response is received, set appropriate state variables.
    useEffect(() => {
        const response = projectsFetcher.serverResponse;
        if (response?.length > 0) {

            //sort the projects alphabetically by name and add isPendingReview flag
            const sortedProjects = response.toSorted((a, b) => a.projectTitle?.localeCompare(b.projectTitle))
                .map(project => ({
                    ...project,
                    isPendingReview: project.status?.toLowerCase() === "active" && markForReview(project.reviewedDate)
                }))
                .sort((a, b) => (b.isPendingReview ? 1 : 0) - (a.isPendingReview ? 1 : 0));
            setProjectsData(sortedProjects);
        }
        else if (response?.error) {
            console.error("Error in GET projects request", response.error);
        }
    }, [projectsFetcher.serverResponse]);

    //Fetch data from API when the component renders.
    useEffect(() => {
        statsFetcher.setRequest({});

        //If loggedin user is super admin or TTI user, fetch all top 105 roads
        if (loggedInUserRole?.toLowerCase() === "super admin" || loggedInUserRole?.toLowerCase() === "tti user") {
            roadwaysFetcher.setRequest({});
            setPageTitle('Top 105 Congested Road Segments')
        }
        else if (auth?.user?.regionAccessibleSet) {

            //set page title
            setPageTitle(`${auth.user.regionAccessibleSet[0]?.accessibles || ''} Top 105 Congested Road Segments`);

            const userRegionsIds = auth.user.regionAccessibleSet.map(region => region.id);
            if (userRegionsIds?.length === 1) {
                const urlWithRegionIds = `${API_URLS["GET_TOP_105_ROADWAYS"]}?district=${userRegionsIds[0]}`;
                roadwaysFetcher.setRequest({ passedUrl: urlWithRegionIds });
            }
        }
    }, [loggedInUserRole]);

    //Once response is received, set appropriate state variables.
    useEffect(() => {
        const response = roadwaysFetcher.serverResponse;
        if (response?.length > 0) {
            setTop105RoadwaysData(response || []);
        }
        else if (response?.error) {
            console.error("Error in GET roadways request", response.error);
        }
    }, [roadwaysFetcher.serverResponse]);

    //Once response is received, set appropriate state variables.
    useEffect(() => {
        const response = statsFetcher.serverResponse;
        if (response?.success && response.data) {
            const regionNames = response.data.map(stat => stat.regionName).filter(name => name !== null);

            //sort regions alphabetically, keeping "All" at the top
            const sortedRegions = regionNames.toSorted((a, b) => {
                if (a.toLowerCase() === 'all') return -1;
                if (b.toLowerCase() === 'all') return 1;
                return a.localeCompare(b);
            });
            setRegionsDropDown(sortedRegions);
            setAllStats(response.data);
        }
        else if (response?.message) {
            console.error("Error in GET roadways request", response.message);
        }
    }, [statsFetcher.serverResponse]);

    //Renders when either regionsDropDown or allStats changes.
    useEffect(() => {
        if (regionsDropDown?.length > 0)
            setSelectedRegion(regionsDropDown[0]);
        if (allStats?.length > 0 && regionsDropDown?.length > 0)
            setSelectedStat(allStats.filter(stat => stat.regionName === regionsDropDown[0])[0]);
    }, [regionsDropDown, allStats]);

    //When region drop down is clicked.
    const handleRegionChange = (event, newValue) => {
        if (newValue) {
            //set selected region
            setSelectedRegion(newValue);

            //change values as per selected region
            setSelectedStat(allStats.filter(stat => stat.regionName === newValue)[0]);
        }
    };

    //Invoked when a road segment is selected.
    const handleRoadClick = (feature) => {
        setFeatureId(feature.id);
    };

    //Renders different events for each road segment.
    function onEachRoad(feature, layer) {
        const { road: roadName, from_road: fromRoad, to_road: toRoad, arank: rank } = feature.properties;
        const segmentId = feature.id;
        layerRefs.current[segmentId] = layer;
        const fontFamily = themes.default.typography.fontFamily;
        const visuallyHidden = "position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0,0,0,0);border:0;";
        layer.bindPopup(`
            <div id="popup-container-${segmentId}" style="min-width:160px;font-family:${fontFamily};outline:none;" tabindex="-1">
                <h3 id="popup-title-${segmentId}" style="text-align:center;font-weight:bold;margin:0 0 8px;font-size:1rem">
                    Road Segment Details<span style="${visuallyHidden}">.</span>
                </h3>          
                <div id="popup-desc-${segmentId}">
                <p style="margin:4px 0;font-size:0.75rem"><strong>Roadname:</strong> ${roadName}<span style="${visuallyHidden}">.</span></p>
                <p style="margin:4px 0;font-size:0.75rem"><strong>From Road:</strong> ${fromRoad}<span style="${visuallyHidden}">.</span></p>
                <p style="margin:4px 0;font-size:0.75rem"><strong>To Road:</strong> ${toRoad}<span style="${visuallyHidden}">.</span></p>
                <p style="margin:4px 0;font-size:0.75rem"><strong>Rank:</strong> ${rank}<span style="${visuallyHidden}">.</span></p>
                </div>
            </div>
        `);
        layer.on("popupopen", () => {
            const popupEl = layer.getPopup().getElement();
            if (popupEl) {
                requestAnimationFrame(() => {
                    const container = document.getElementById(`popup-container-${segmentId}`);
                    container?.focus();
                });
            }
        });
        layer.on({
            click: handleRoadClick.bind(null, feature),
        });
    }

    const gridRef = useRef(null);

    // 508 compliance fix to ensure header sort buttons in the table are focusable and operable via keyboard
    useEffect(() => {
        if (!gridRef.current) return;
        const buttons = gridRef.current.querySelectorAll(
            '.MuiDataGrid-iconButtonContainer button'
        );
        buttons.forEach((btn) => btn.setAttribute('tabindex', '0'));
    }, [filteredNeedsReviewRows, needsReviewColumns]);
    return (
        <>
            {/* Welcome user */}
            <Grid item xs={12}>
                <Typography color="inherit" variant="h8">Hello {firstName}!</Typography>
            </Grid>

            {/* Top 105 roads heading */}
            <Grid item xs={12}>
                <Title title={pageTitle} />
            </Grid>

            {/* Map */}
            <Grid item xs={12}>
                {!roadwaysFetcher.isLoading && (
                    <Alert
                        role="status"
                        severity="info"
                        sx={{ marginRight: "20px", marginBottom: "8px" }}
                    >
                        Click on any road segment to view its details.
                    </Alert>
                )}
                <Texas100RoadSegmentsMap
                    data={top105RoadwaysData}
                    isLoading={roadwaysFetcher.isLoading}
                    onEachFeature={onEachRoad}
                    ariaLabel="Top 105 Congested Road Segments interactive map"
                    mapStyle={{ marginRight: "20px" }}
                    featureId={featureId}
                    layerRefs={layerRefs}
                    onFeatureSelect={setFeatureId}
                />
            </Grid>

            {/* Statistics Heading */}
            <Grid item xs={12}>
                <Title title="Statistics" />
            </Grid>

            {statsFetcher.isLoading && (
                <Alert severity="info" style={{ marginRight: "20px" }}>
                    Loading statistics. Thank you for your patience.
                </Alert>
            )}

            {!statsFetcher.isLoading && (
                <>
                    {/* Statistics */}
                    <Grid container spacing={2} sx={{ paddingRight: "20px", paddingBottom: "10px" }}>

                        {/* Region names drop down */}
                        <Grid item xs={2}>
                            <StyledPaper elevation={3}>
                                <StyledBox>
                                    <TileHeading title="Select Region" id="region-select-label" asLabel={true} passedHtmlFor="region-autocomplete" />
                                    <Autocomplete
                                        id="region-autocomplete"
                                        options={regionsDropDown}
                                        value={selectedRegion || null}
                                        onChange={handleRegionChange}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                variant="outlined"
                                                onKeyDown={(e) => {
                                                    if (e.key === "ArrowDown") {
                                                        e.currentTarget
                                                            .querySelector('button[aria-label="Open"]')
                                                            ?.click();
                                                    }
                                                }}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    id: 'region-autocomplete',
                                                    "aria-label": "Region selection",
                                                }}
                                                InputProps={{
                                                    ...params.InputProps,
                                                    componentsProps: {
                                                        clearIndicator: {
                                                            tabIndex: 0,
                                                        },
                                                    },
                                                }}
                                            />
                                        )}
                                        componentsProps={{
                                            clearIndicator: {
                                                tabIndex: 0,
                                                "aria-label": "Clear region",
                                            },
                                            popupIndicator: {
                                                tabIndex: 0,
                                                "aria-label": "Open region dropdown",
                                            },
                                            popper: {
                                                placement: "bottom-start",
                                            },
                                        }}
                                        ListboxProps={{
                                            id: "region-autocomplete-listbox",
                                            style: { maxHeight: "250px" },
                                        }}
                                        sx={{
                                            width: '70%',
                                            '& .MuiInputBase-root': {
                                                height: '30px',
                                                padding: '0 9px'
                                            },
                                            '& .MuiAutocomplete-input': {
                                                padding: '0 !important'
                                            }
                                        }}
                                    />
                                </StyledBox>
                            </StyledPaper>

                        </Grid>


                        {/* Total number of projects */}
                        <Grid item xs={2}>
                            <StyledPaper elevation={3}>
                                <StyledBox>
                                    <TileHeading title="Total Projects" />
                                    <StyledTypography>{selectedStat.totalProjects}</StyledTypography>
                                </StyledBox>
                            </StyledPaper>
                        </Grid>

                        {/* Needs Review projects */}
                        <Grid item xs={2}>
                            <Tooltip title={showNeedsReview ? "Click to collapse details" : "Click to view projects needing review"} arrow>
                                <StyledPaper
                                    elevation={showNeedsReview ? 6 : 3}
                                    onClick={() => setShowNeedsReview(prev => !prev)}
                                    sx={{
                                        cursor: 'pointer',
                                        border: showNeedsReview ? `2px solid ${themes.default.palette.error.dark}` : '2px solid transparent',
                                        transition: 'all 0.2s ease-in-out',
                                        '&:hover': {
                                            elevation: 6,
                                            borderColor: themes.default.palette.error.dark,
                                            transform: 'translateY(-2px)',
                                        },
                                    }}
                                >
                                    <StyledBox

                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                                            <TileHeading title="Needs Review" />
                                            <NeedsReviewIcon style={{ color: themes.default.palette.error.dark, marginLeft: '-12px' }} />
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} tabIndex={0}
                                            role="button"
                                            aria-expanded={showNeedsReview}
                                            aria-label={showNeedsReview ? "Collapse Needs Review section" : "Expand Needs Review section"}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    setShowNeedsReview((prev) => !prev);
                                                }
                                            }}>
                                            <StyledTypography>
                                                {projectsFetcher.isLoading
                                                    ? <CircularProgress size={20} sx={{ color: themes.default.palette.error.dark }} />
                                                    : (filteredNeedsReviewRows ?? []).length
                                                }
                                            </StyledTypography>
                                            {showNeedsReview
                                                ? <ExpandLessIcon fontSize="small" sx={{ color: themes.default.palette.error.dark }} />
                                                : <ExpandMoreIcon fontSize="small" sx={{ color: themes.default.palette.error.dark }} />
                                            }
                                        </Box>
                                    </StyledBox>
                                </StyledPaper>
                            </Tooltip>
                        </Grid>

                        {/* Active projects */}
                        <Grid item xs={2}>
                            <StyledPaper elevation={3}>
                                <StyledBox>
                                    <TileHeading title="Active" />
                                    <StyledTypography>{selectedStat.activeProjects}</StyledTypography>
                                </StyledBox>
                            </StyledPaper>
                        </Grid>

                        {/* Inactive projects */}
                        <Grid item xs={2}>
                            <StyledPaper elevation={3}>
                                <StyledBox>
                                    <TileHeading title="Inactive" />
                                    <StyledTypography>{selectedStat.inactiveProjects}</StyledTypography>
                                </StyledBox>
                            </StyledPaper>
                        </Grid>

                        {/* Draft projects */}
                        <Grid item xs={2}>
                            <StyledPaper elevation={3}>
                                <StyledBox>
                                    <TileHeading title="Draft" />
                                    <StyledTypography>{selectedStat.draftProjects}</StyledTypography>
                                </StyledBox>
                            </StyledPaper>
                        </Grid>
                    </Grid>

                    {/* Needs Review Table */}
                    {showNeedsReview && (
                        <Grid container ref={needsReviewTableRef} sx={{ paddingRight: "20px", paddingTop: "20px" }}>
                            <Grid item xs={12}>
                                <Box ref={gridRef} sx={{
                                    width: '100%',
                                }}>
                                    <StyledDataGrid
                                        rows={filteredNeedsReviewRows || []}
                                        columns={needsReviewColumns}
                                        disableColumnMenu
                                        getRowId={(row) => row.projectId || row.id}
                                        initialState={{
                                            pagination: { paginationModel: { pageSize: 10 } },
                                        }}
                                        pageSizeOptions={[10, 15, 20, 25]}
                                        disableRowSelectionOnClick
                                        autoHeight
                                        slots={{ toolbar: CustomToolbar }}
                                        slotProps={{
                                            toolbar: {
                                                totalCount: needsReviewRows?.length || 0,
                                                filter: {
                                                    rowsCount: (filteredNeedsReviewRows ?? []).length,
                                                    columns: needsReviewColumns,
                                                    active: activeFilter,
                                                    onChange: (val) => setActiveFilter(val)
                                                },
                                                exportedFilename: `Projects_Pending_Review_${selectedRegion === 'All' ? 'All_Regions' : selectedRegion} `
                                            },
                                            pagination: {
                                                labelRowsPerPage: 'Rows per page:',
                                                SelectProps: {
                                                    native: true,
                                                    inputProps: {
                                                        'aria-label': 'Rows per page',
                                                        'id': 'pagination-select-input'
                                                    }
                                                }
                                            },
                                        }}
                                        localeText={{ noRowsLabel: 'No projects pending review.' }}
                                        sx={{ //508 compliance style to focus on column header
                                            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
                                                outline: '2px solid #1976d2',
                                                outlineOffset: '-2px',
                                            },
                                            // Fix MUI collapsing the button container when not hovered
                                            '& .MuiDataGrid-iconButtonContainer': {
                                                visibility: 'visible !important',
                                                width: 'auto !important',
                                                opacity: '1 !important',
                                            },
                                            '& .MuiDataGrid-iconButtonContainer button': {
                                                visibility: 'visible !important',
                                                opacity: '1 !important',
                                            },
                                        }}
                                    />
                                </Box>
                            </Grid>
                        </Grid>
                    )}
                </>
            )}
        </>
    );
}